import React, { useState } from 'react';
import {
    Box,
    Button,
    makeStyles,
    Table,
    TableHead,
    TableBody,
    TableCell,
    TableRow,
    Typography,
} from '@material-ui/core';
import { useTranslation } from 'react-i18next';
import type { FormField } from '../interfaces';
import { Block } from '@klp/design';
import { LeverandorListRow } from './FormFields/LeverandorListRow';
import { useField, useFormikContext } from 'formik';
import { Add } from '@material-ui/icons';
import { getLocalizedLabel } from '../utils';

const useStyles = makeStyles((theme) => ({
    leverandorListContainer: {
        paddingTop: theme.spacing(2),
        paddingBottom: theme.spacing(2),

        '& .MuiTableCell-root': {
            paddingTop: theme.spacing(1),
            paddingBottom: theme.spacing(1),
            paddingLeft: theme.spacing(0.5),
            paddingRight: theme.spacing(0.5),
        },
    },

    actionButtonContainer: {
        display: 'flex',
        justifyContent: 'flex-end',
    },

    labelContainer: {
        display: 'flex',
        alignItems: 'center',
    },

    labelRequired: {
        color: theme.palette.error.main,
        fontSize: '1.5rem',
    },
    errorText: {
        color: theme.palette.error.main,
    },

    responsiveTable: {
        width: '100%',
    },
    colgroupDesktop: {
        [theme.breakpoints.down('sm')]: {
            display: 'none',
        },
    },
    headerRow: {
        [theme.breakpoints.down('sm')]: {
            display: 'none',
        },
    },
    mobileCard: {
        [theme.breakpoints.down('sm')]: {
            borderBottom: '1px solid #ddd',
            padding: theme.spacing(2),
            display: 'flex',
            flexDirection: 'column',
        },
    },
}));

export type Leverandor = {
    prosessInstansId: string;
    organisasjonsnummer?: string;
    leverandorInfo?: string;
    navn?: string;
    pris?: number;
    prisKorrigert?: number;
    leverandorKlassifisering?: string;
    valgtLeverandor?: boolean;
};

export type LeverandorListSelectorProps = {
    field: FormField;
    isReadOnly: boolean;
    isRequired: boolean;
    prosessInstansId: string;
};

export const LeverandorListSelector = ({
    field,
    isReadOnly,
    isRequired,
    prosessInstansId,
}: LeverandorListSelectorProps) => {
    const { t } = useTranslation();
    const classes = useStyles();
    const [formikField, meta] = useField<string | null>(field?.id as string);
    const { setFieldValue } = useFormikContext();

    const parseLeverandorList = (
        jsonString: string | null,
    ): Array<Leverandor> => {
        const initialValue = isRequired ? [{ prosessInstansId }] : [];

        if (!jsonString || jsonString === '') return initialValue;

        try {
            const parsed = JSON.parse(jsonString) as Array<Leverandor>;
            return parsed;
        } catch (e) {
            return [{ prosessInstansId }];
        }
    };
    const [leverandorList, setLeverandorList] = useState<Array<Leverandor>>(
        parseLeverandorList(formikField.value),
    );

    const handleChangedLeverandor = (
        changedIndex: number,
        newLeverandor: Leverandor,
    ) => {
        const updatedList = leverandorList.map((leverandor, index) => {
            if (index === changedIndex) {
                return newLeverandor;
            }
            return leverandor;
        });
        updateLeverandorList(updatedList);
    };

    const handleDeleteLeverandor = (deleteIndex: number) => {
        const deletedList = leverandorList.filter((l, i) => i !== deleteIndex);
        updateLeverandorList(deletedList);
    };

    const addLeverandor = () => {
        const addedList = [
            ...leverandorList,
            {
                prosessInstansId: prosessInstansId as string,
            },
        ];
        updateLeverandorList(addedList);
    };

    const updateLeverandorList = (updatedList: Array<Leverandor>) => {
        setLeverandorList(updatedList);
        const leverandorListString = JSON.stringify(updatedList);
        setFieldValue(field?.id as string, leverandorListString);
    };

    return (
        <Box id={field?.id}>
            <Block className={classes.labelContainer}>
                <Typography>
                    {getLocalizedLabel(
                        `project.formFields.${field?.id}`,
                        field?.label,
                        field?.id,
                    )}
                </Typography>
                {isRequired && (
                    <Typography className={classes.labelRequired}>
                        &nbsp;*
                    </Typography>
                )}
            </Block>
            <Block className={classes.leverandorListContainer}>
                <Table className={classes.responsiveTable}>
                    <colgroup className={classes.colgroupDesktop}>
                        <col style={{ width: '40%' }} />
                        <col style={{ width: '20%' }} />
                        <col style={{ width: '20%' }} />
                        <col style={{ width: '10%' }} />
                        <col style={{ width: '5%' }} />
                        <col style={{ width: '5%' }} />
                    </colgroup>
                    <TableHead>
                        <TableRow className={classes.headerRow}>
                            <TableCell>
                                <Box className={classes.labelContainer}>
                                    {t(
                                        'prosjekt.leverandor.organizationSelect',
                                    )}
                                    {isRequired && (
                                        <Typography
                                            className={classes.labelRequired}
                                        >
                                            &nbsp;*
                                        </Typography>
                                    )}
                                </Box>
                            </TableCell>
                            <TableCell>
                                {t('prosjekt.leverandor.price')}
                            </TableCell>
                            <TableCell>
                                {t('prosjekt.leverandor.priceRevised')}
                            </TableCell>
                            <TableCell>
                                {t('prosjekt.leverandor.klassifisering')}
                            </TableCell>
                            <TableCell>
                                {t('prosjekt.leverandor.selected')}
                            </TableCell>
                            <TableCell />
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {leverandorList.map((value, rowIndex) => (
                            <LeverandorListRow
                                key={`leverandor ${value.organisasjonsnummer} ${rowIndex}`}
                                leverandor={value}
                                leverandorIndex={rowIndex}
                                isReadOnly={isReadOnly}
                                isRequired={isRequired}
                                onChange={(val) =>
                                    handleChangedLeverandor(rowIndex, val)
                                }
                                canDeleteRow={
                                    !isRequired || leverandorList.length > 1
                                }
                                onDeleteRow={() =>
                                    handleDeleteLeverandor(rowIndex)
                                }
                            />
                        ))}
                    </TableBody>
                </Table>
            </Block>
            <Block>
                {meta.touched && meta.error && (
                    <Typography className={classes.errorText}>
                        {meta.error}
                    </Typography>
                )}
            </Block>
            {!isReadOnly && (
                <Block className={classes.actionButtonContainer}>
                    <Button
                        disabled={leverandorList.some(
                            (l) => !l.organisasjonsnummer,
                        )}
                        variant="outlined"
                        onClick={addLeverandor}
                        endIcon={<Add />}
                    >
                        {t('prosjekt.leverandor.add')}
                    </Button>
                </Block>
            )}
        </Box>
    );
};
